import { useState } from 'react';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import { Alert, Button, Card, EmptyState, Icon, Input, Modal, Spinner, StatusPill, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTimePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface AdminUserRow {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  isPlatformAdmin: number;
  deletedAt: string | null;
  createdAt: string;
  memberships: number;
}

interface UsersPayload {
  users: AdminUserRow[];
}

interface TogglePayload {
  ok: boolean;
  isPlatformAdmin: boolean;
}

export function AdminUsersPage(): JSX.Element {
  usePageMeta('Usuários — administração', 'Busca de contas e gestão de acesso administrativo.', '/admin/usuarios');

  const toast = useToast();
  const { user } = useAuth();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<AdminUserRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const resource = useResource<UsersPayload>(
    () => api.get<UsersPayload>(`/api/admin/users?q=${encodeURIComponent(query)}&limit=200`),
    [query]
  );

  async function confirmToggle(): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.post<TogglePayload>(`/api/admin/users/${pending.id}/toggle-admin`);
      toast(
        res.isPlatformAdmin
          ? tr('{name} agora é administrador da plataforma.', { name: pending.fullName })
          : tr('{name} perdeu o acesso administrativo.', { name: pending.fullName }),
        'success'
      );
      setPending(null);
      await resource.reload();
    } catch (e) {
      // O backend bloqueia a auto-remoção com code "self_change": mostramos a mensagem real.
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  const rows = resource.data?.users ?? [];

  return (
    <AdminScreen
      title={tr("Usuários da plataforma")}
      description={tr("Busque contas por nome ou e-mail e conceda ou revogue o acesso administrativo. Toda mudança de papel é registrada na auditoria.")}
    >
      <form
        className="mb-4 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(term.trim());
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <label className="nl-label" htmlFor="admin-users-search">{tr("Buscar usuário")}</label>
          <Input
            id="admin-users-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={tr("Nome ou e-mail")}
            maxLength={80}
          />
        </div>
        <Button type="submit" variant="secondary" disabled={resource.loading}>
          <Icon name="search" /> {tr("Buscar")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={resource.loading}
          onClick={() => {
            setTerm('');
            setQuery('');
            void resource.reload();
          }}
        >
          {tr("Limpar")}
        </Button>
        <Button type="button" variant="ghost" disabled={resource.loading} onClick={() => void resource.reload()}>
          {resource.loading ? <Spinner label="" /> : <Icon name="chart" />} {tr("Atualizar")}
        </Button>
      </form>

      {query ? <p className="nl-hint mb-3">{tr("Resultados para")} <strong>{query}</strong>.</p> : null}

      <LoadOrError loading={resource.loading} error={resource.error} />

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState title={tr("Nenhum usuário encontrado")} description={tr("Ajuste a busca ou limpa o filtro para ver as contas mais recentes.")} />
      ) : null}

      {rows.length > 0 ? (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[56rem] text-left text-sm">
            <caption className="sr-only">{tr("Lista de usuários da plataforma")}</caption>
            <thead className="bg-surface text-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Usuário")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Telefone")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Empresas")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Situação")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Criado em")}</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">{tr("Ações")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isSelf = row.id === user?.id;
                return (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-4 py-3">
                      <p className="font-semibold">{row.fullName}</p>
                      <p className="nl-hint">{row.email}</p>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{row.phone ?? '—'}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.memberships} {row.memberships === 1 ? 'vínculo' : 'vínculos'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {row.isPlatformAdmin ? (
                          <StatusPill tone="positive">{tr("Administrador da plataforma")}</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">{tr("Usuário comum")}</StatusPill>
                        )}
                        {row.deletedAt ? <StatusPill tone="danger">{tr("Conta desativada")}</StatusPill> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{dateTimePt(row.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant={row.isPlatformAdmin ? 'danger' : 'secondary'}
                        disabled={resource.loading}
                        onClick={() => {
                          setActionError(null);
                          setPending(row);
                        }}
                      >
                        {row.isPlatformAdmin ? 'Remover admin' : 'Tornar admin'}
                      </Button>
                      {isSelf ? <p className="nl-hint mt-1">{tr("É a sua conta")}</p> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Modal
        open={pending !== null}
        onClose={() => (busy ? undefined : setPending(null))}
        title={pending?.isPlatformAdmin ? 'Revogar acesso administrativo' : 'Conceder acesso administrativo'}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setPending(null)}>{tr("Voltar")}</Button>
            <Button
              variant={pending?.isPlatformAdmin ? 'danger' : 'primary'}
              loading={busy}
              disabled={busy}
              onClick={() => void confirmToggle()}
            >
              {pending?.isPlatformAdmin ? tr('Revogar acesso') : tr('Conceder acesso')}
            </Button>
          </>
        }
      >
        {pending ? (
          <div className="space-y-3">
            <p>
              {tr("Você está prestes a")} <strong>{pending.isPlatformAdmin ? 'revogar' : 'conceder'}</strong> {tr("o papel de administrador da plataforma para")} <strong>{pending.fullName}</strong> ({pending.email}).
            </p>
            <Alert tone="warning" title={tr("Consequência")}>
              {pending.isPlatformAdmin
                ? 'Sem o papel de administrador essa pessoa perde o acesso a todas as telas de /admin: usuários, empresas, assinaturas, planos, categorias, auditoria, configurações e moderação de avaliações. A ação é imediata e fica registrada em Auditoria.'
                : 'Com o papel de administrador essa pessoa passa a ver todos os dados da plataforma (usuários, empresas, assinaturas, transações e configurações) e a executar ações irreversíveis. A ação é imediata e fica registrada em Auditoria.'}
            </Alert>
            {pending.id === user?.id && pending.isPlatformAdmin ? (
              <Alert tone="danger" title={tr("Auto-remoção bloqueada")}>
                {tr("Você não pode remover o seu próprio acesso administrativo por aqui — o backend recusa com «Você não pode remover seu próprio acesso administrativo por aqui.». Peça a outro administrador.")}
              </Alert>
            ) : null}
            {actionError ? <Alert tone="danger" title={tr("Ação não concluída")}>{actionError}</Alert> : null}
          </div>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}
